<template>
  <div
    ref="outerContainerRef"
    data-testid="toast"
    class="
      tw-flex
      tw-items-center
      tw-justify-between
      tw-gap-3
      tw-px-4
      tw-py-[11px]
      tw-font-sans
      tw-bg-sf-snow-lighter
      tw-border
      tw-border-solid
      tw-border-sf-snow-dark
      tw-rounded-lg
      tw-shadow-toast
      tw-text-sm
      tw-leading-4
      tw-text-sf-gray-light
    "
    :class="containerClass"
  >
    <div class="tw-flex tw-gap-3 tw-items-center">
      <i
        ref="iconContainerRef"
        class="tw-text-base fa"
        :class="icon"
        data-testid="toast-icon"
      />
      <div class="tw-flex tw-flex-col tw-gap-0.5">
        <div class="tw-text-sf-gray tw-font-medium">
          <SfEllipsis :length="availableWidthForText" :tooltip="titleTooltip">
            <slot name="title" />
          </SfEllipsis>
        </div>
        <div>
          <SfEllipsis :height="32" :tooltip="messageTooltip">
            <slot name="message" />
          </SfEllipsis>
        </div>
      </div>
    </div>
    <div ref="actionsContainerRef" class="tw-flex tw-items-center">
      <div>
        <slot name="action" />
      </div>
      <SfButtonIcon
        class="!tw-text-sf-gray-light"
        seamless
        icon="close"
        data-testid="toast-dismiss"
        @click="handleDismiss"
      />
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

import {
  ColorVariants,
  ColorVariantsType,
  Sizes,
  SizesType,
} from '@app/shared/constants'
import SfButtonIcon from '../sf-button/sf-button-icon.vue'
import SfEllipsis from '../sf-ellipsis/sf-ellipsis.vue'

interface Props {
  autoDismissDelay?: number
  size?: Extract<SizesType, 'sm' | 'md' | 'lg'>
  titleTooltip?: string
  messageTooltip?: string
  variant?: ColorVariantsType
}

const {
  autoDismissDelay,
  size = Sizes.MD,
  variant = ColorVariants.DEFAULT,
} = defineProps<Props>()

const emits =
  defineEmits<{
    dismiss: []
  }>()

const outerContainerRef = ref<HTMLElement | null>(null)
const iconContainerRef = ref<HTMLElement | null>(null)
const actionsContainerRef = ref<HTMLElement | null>(null)

let autoDismissTimeout: number | null = null

onMounted(() => {
  if (autoDismissDelay) {
    autoDismissTimeout = window.setTimeout(() => {
      emits('dismiss')
    }, autoDismissDelay)
  }
})

onBeforeUnmount(() => {
  if (autoDismissTimeout) {
    window.clearTimeout(autoDismissTimeout)
  }
})

const containerClass = computed(() => {
  return {
    'tw-w-[240px]': size === Sizes.SM,
    'tw-w-[360px]': size === Sizes.MD,
    'tw-w-[600px]': size === Sizes.LG,
  }
})

const icon = computed(() => {
  return {
    'fa-info-circle tw-text-sf-gray-light': variant === ColorVariants.DEFAULT,
    'fa-check-circle tw-text-sf-success': variant === ColorVariants.SUCCESS,
    'fa-info-circle tw-text-sf-warning': variant === ColorVariants.WARNING,
    'fa-exclamation-triangle tw-text-sf-danger':
      variant === ColorVariants.DANGER,
    'fa-info-circle tw-text-sf-primary': variant === ColorVariants.INFO,
    'fa-wand-magic-sparkles tw-text-sf-ai': variant === ColorVariants.AI,
  }
})

const availableWidthForText = computed(() => {
  if (
    !outerContainerRef.value ||
    !iconContainerRef.value ||
    !actionsContainerRef.value
  ) {
    return 300
  }

  return (
    outerContainerRef.value.offsetWidth -
    iconContainerRef.value.offsetWidth -
    actionsContainerRef.value.offsetWidth -
    32 -
    12 -
    12 -
    2
  )
})

function handleDismiss(): void {
  emits('dismiss')
}
</script>
